import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './queries';
import { Project, User } from 'types';

interface ProjectsResponse {
  success: boolean;
  projects: Project[];
}

interface ProjectResponse {
  success: boolean;
  project: Project;
}

interface MembersResponse {
  success: boolean;
  members: User[];
}

interface CreateProjectRequest {
  name: string;
  description?: string;
  memberIds?: string[];
}

export const projectApi = createApi({
  reducerPath: 'projectApi',
  baseQuery: axiosBaseQuery({
    baseUrl: import.meta.env.VITE_APP_BASE_URL as string,
  }),
  tagTypes: ['Project', 'ProjectMember'],
  endpoints: (builder) => ({
    // Project CRUD (workspace-scoped)
    getProjects: builder.query<Project[], string>({
      query: (workspaceId) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/`, 
        method: 'get'
      }),
      transformResponse: (response: ProjectsResponse) => response.projects,
      providesTags: ['Project'],
    }),
    getProject: builder.query<Project, { workspaceId: string; projectId: string }>({
      query: ({ workspaceId, projectId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/`, 
        method: 'get' 
      }),
      transformResponse: (response: ProjectResponse) => response.project,
      providesTags: ['Project'],
    }),
    createProject: builder.mutation<Project, { workspaceId: string; data: CreateProjectRequest }>({
      query: ({ workspaceId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/`, 
        method: 'post', 
        data 
      }),
      transformResponse: (response: ProjectResponse) => response.project,
      invalidatesTags: ['Project'],
    }),
    updateProject: builder.mutation<Project, { workspaceId: string; projectId: string; data: Partial<Project> }>({
      query: ({ workspaceId, projectId, data }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/`, 
        method: 'patch', 
        data 
      }),
      transformResponse: (response: ProjectResponse) => response.project,
      invalidatesTags: ['Project', 'ProjectMember'], // Invalidate ProjectMember when project is updated (memberIds might have changed)
    }),
    deleteProject: builder.mutation<void, { workspaceId: string; projectId: string }>({
      query: ({ workspaceId, projectId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/`, 
        method: 'delete' 
      }),
      invalidatesTags: ['Project'],
    }),
    archiveProject: builder.mutation<Project, { workspaceId: string; projectId: string }>({
      query: ({ workspaceId, projectId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/archive/`, 
        method: 'post' 
      }),
      transformResponse: (response: ProjectResponse) => response.project,
      invalidatesTags: ['Project'],
    }),
    unarchiveProject: builder.mutation<Project, { workspaceId: string; projectId: string }>({
      query: ({ workspaceId, projectId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/unarchive/`, 
        method: 'post' 
      }),
      transformResponse: (response: ProjectResponse) => response.project,
      invalidatesTags: ['Project'],
    }),

    // Project Members
    getProjectMembers: builder.query<User[], { workspaceId: string; projectId: string }>({
      query: ({ workspaceId, projectId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/members/`, 
        method: 'get' 
      }),
      transformResponse: (response: MembersResponse) => response.members,
      providesTags: (result, error, { projectId }) => [
        { type: 'ProjectMember', id: projectId },
        'ProjectMember'
      ],
    }),
    addProjectMember: builder.mutation<void, { workspaceId: string; projectId: string; userId: string }>({
      query: ({ workspaceId, projectId, userId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/members/add/`, 
        method: 'post',
        data: { userId }
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'ProjectMember', id: projectId },
        'ProjectMember',
        'Project'
      ],
    }),
    removeProjectMember: builder.mutation<void, { workspaceId: string; projectId: string; userId: string }>({
      query: ({ workspaceId, projectId, userId }) => ({ 
        url: `/v1/workspaces/${workspaceId}/projects/${projectId}/members/${userId}/`, 
        method: 'delete' 
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'ProjectMember', id: projectId },
        'ProjectMember',
        'Project'
      ],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useArchiveProjectMutation,
  useUnarchiveProjectMutation,
  useGetProjectMembersQuery,
  useAddProjectMemberMutation,
  useRemoveProjectMemberMutation,
} = projectApi;
